import SectionHeader from "../../../../components/SectionHeader/SectionHeader.tsx";
import { translateError } from "../../../../constants/errorMessages.ts";
import { INSURERS, insurerMonogram } from "../../../../constants/insurers.ts";
import { isDefaultProviderEnabled } from "../model.ts";
import type { LifecycleController } from "../useLifecycle.ts";
import { CARD } from "./shared.tsx";

export function OffersPanel({
  controller,
}: {
  controller: LifecycleController;
}) {
  const { quote, provider, stale, busy, quoteForm, capabilities } = controller;
  const { setProvider } = quoteForm;
  return (
    <>
      {quote && (
        <fieldset className={CARD}>
          <SectionHeader title="Оферти" />
          <div className={`p-4 pt-3 ${stale ? "opacity-40" : ""}`}>
            <table className="block w-full text-sm">
              <colgroup className="hidden">
                <col className="w-[38%]" />
                <col className="w-[44%]" />
                <col className="w-[18%]" />
              </colgroup>
              <tbody className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                {quote.offers
                  .filter((o) => isDefaultProviderEnabled(o.provider))
                  .map((o) => {
                    // Официалното име от справочника; `displayName` от бекенда е
                    // резервен вариант за застраховател, който още няма запис тук.
                    const name =
                      INSURERS[o.provider]?.name ??
                      capabilities.find((c) => c.provider === o.provider)
                        ?.displayName ??
                      o.provider;
                    const logo = INSURERS[o.provider]?.logo;
                    const isSel = provider === o.provider;
                    return (
                      <tr
                        key={o.provider}
                        className={`flex min-h-[126px] min-w-0 flex-wrap items-center gap-4 rounded-lg border border-stroke bg-white p-4 transition-all dark:border-strokedark dark:bg-boxdark ${
                          isSel
                            ? "border-primary shadow-md ring-2 ring-primary/15"
                            : "hover:border-primary/40 hover:shadow-md"
                        }`}
                      >
                        <td className="min-w-0 flex-1 p-0 align-middle font-medium text-black dark:text-white">
                          <div className="flex min-w-0 items-center gap-3">
                            {logo ? (
                              <img
                                src={logo}
                                alt=""
                                aria-hidden="true"
                                className="h-12 w-20 shrink-0 rounded-md bg-white object-contain p-1.5"
                              />
                            ) : (
                              /* Монограм, докато лого няма — редът не бива да
                                 се измества спрямо останалите. */
                              <span
                                aria-hidden="true"
                                className="flex h-12 w-20 shrink-0 items-center justify-center rounded-md bg-gray-2 text-xs font-semibold text-body dark:bg-meta-4 dark:text-bodydark"
                              >
                                {insurerMonogram(o.provider)}
                              </span>
                            )}
                            <span className="min-w-0 break-words leading-snug">
                              {name}
                            </span>
                          </div>
                        </td>
                        <td className="min-w-0 basis-full p-0 align-middle sm:basis-auto">
                          {o.available && o.premium ? (
                            <span className="flex flex-wrap items-baseline gap-x-1.5 leading-tight">
                              <span className="whitespace-nowrap text-lg font-bold text-black dark:text-white">
                                {o.premium.eur.toFixed(2)} EUR
                              </span>
                              <span className="whitespace-nowrap font-normal text-body dark:text-bodydark">
                                / {o.premium.bgn.toFixed(2)} лв.
                              </span>
                            </span>
                          ) : (
                            /* Отказът е ред с причина — липсващ ред би се разчел
                               като „няма оферта“. Причината се превежда: суровият
                               текст на застрахователя е технически английски. */
                            <span
                              className="block break-words text-xs leading-5 text-body dark:text-bodydark"
                              title={
                                translateError(o.errorCode, o.reason).detail
                              }
                            >
                              {translateError(o.errorCode, o.reason).text}
                            </span>
                          )}
                        </td>
                        <td className="ml-auto p-0 text-right align-middle">
                          {o.available && o.canIssue && (
                            <button
                              disabled={stale || busy !== null}
                              onClick={() => setProvider(o.provider)}
                              className={`min-w-28 whitespace-nowrap rounded-md px-4 py-2 text-xs font-semibold transition-colors disabled:opacity-40 ${
                                isSel
                                  ? "bg-primary text-white"
                                  : "bg-primary/10 text-primary hover:bg-primary hover:text-white dark:bg-white/10 dark:text-white"
                              }`}
                            >
                              {isSel ? "Избрана" : "Избери"}
                            </button>
                          )}
                          {o.available && !o.canIssue && (
                            <span className="inline-block text-right text-[11px] leading-4 text-body dark:text-bodydark">
                              Издаването не е налично
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </fieldset>
      )}
    </>
  );
}
